/**
 * Cart drawer behaviour.
 *
 * Skeleton ships no cart JS at all, so this is the whole of it: intercept
 * add-to-cart submissions, run quantity changes and removals through the
 * Ajax Cart API, and re-render the drawer using bundled section rendering
 * rather than reloading the page.
 *
 * Every entry point degrades: the bag icon and add-to-cart forms keep
 * working as ordinary links and form posts when this script is absent or
 * a request fails.
 */
(function () {
  const SECTION_ID = 'cart-drawer';
  const routes = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  const dialog = document.getElementById(SECTION_ID);
  if (!dialog) return;

  /** Read the drawer's rendered markup out of a bundled section response. */
  function contentFromSection(html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    return parsed.querySelector('[data-cart-drawer-content]');
  }

  function open() {
    if (!dialog.open) dialog.showModal();
  }

  /**
   * Keep every bag-count badge in the header in step with the cart, and say
   * the new count out loud.
   *
   * Adding, removing and changing quantity all happen without a page load
   * and without moving focus, so a screen reader user gets no confirmation
   * at all unless one is announced deliberately. The drawer opening is the
   * visual confirmation; this is the spoken one.
   */
  function syncCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach((node) => {
      node.textContent = count;
      node.hidden = count === 0;
    });

    const drawer = document.querySelector('.cart-drawer__title');
    if (window.announce && drawer) window.announce(drawer.textContent.trim());
  }

  /** Swap in freshly rendered drawer markup and rebind nothing — events are delegated. */
  function render(sections) {
    const markup = sections && sections[SECTION_ID];
    if (!markup) return;

    const next = contentFromSection(markup);
    const current = dialog.querySelector('[data-cart-drawer-content]');
    if (next && current) current.replaceWith(next);
  }

  async function post(path, body) {
    const response = await fetch(`${routes}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...body, sections: SECTION_ID }),
    });

    if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
    return response.json();
  }

  async function changeLine(key, quantity) {
    const cart = await post('cart/change.js', { id: key, quantity });
    render(cart.sections);
    syncCount(cart.item_count);
  }

  /**
   * Swap a line for a different variant of the same product.
   *
   * The Ajax Cart API cannot change a line's variant in place, so this is
   * a removal followed by an add. Order matters: removing first means a
   * shopper who already holds the target variant ends with one merged line
   * rather than two, and a failure leaves the cart short rather than double.
   */
  async function replaceLine(key, variantId, quantity) {
    await post('cart/change.js', { id: key, quantity: 0 });
    await addItems([{ id: Number(variantId), quantity: quantity || 1 }]);
  }

  /**
   * Add one or more lines, then refresh the drawer and open it.
   *
   * Split out from the form handler so callers that are not a form — the
   * product page's "add the set" button, which adds three variants at once —
   * can go through exactly this path instead of repeating the POST, the
   * re-render and the count sync. Rejects on failure so the caller can
   * restore its own buttons.
   */
  async function addItems(items) {
    if (!items || !items.length) return;

    await post('cart/add.js', { items });

    // add.js does not return the updated totals, so pull the drawer separately.
    const response = await fetch(`${routes}?sections=${SECTION_ID}`);
    const sections = await response.json();
    render(sections);

    const cart = await fetch(`${routes}cart.js`).then((res) => res.json());
    syncCount(cart.item_count);

    open();
  }

  async function addItem(formData) {
    const items = [];
    formData.forEach((value, name) => {
      if (name === 'id') items.push({ id: value, quantity: Number(formData.get('quantity') || 1) });
    });

    return addItems(items);
  }

  /* ---- Open the drawer from the header bag ---- */
  document.querySelectorAll('[data-cart-open]').forEach((trigger) => {
    trigger.addEventListener('click', (event) => {
      event.preventDefault();
      open();
    });
  });

  /* ---- Delegated so re-rendered markup keeps working ---- */
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) {
      dialog.close();
      return;
    }

    if (event.target.closest('[data-cart-close]')) {
      dialog.close();
      return;
    }

    /* ---- The drawer's two nudges ---- */
    const swap = event.target.closest('[data-cart-swap]');
    if (swap) {
      swap.disabled = true;
      replaceLine(swap.dataset.lineKey, swap.dataset.toId, 1)
        .catch(() => {
          window.location.href = `${routes}cart`;
        })
        .finally(() => {
          swap.disabled = false;
        });
      return;
    }

    const add = event.target.closest('[data-cart-add]');
    if (add) {
      add.disabled = true;
      addItems([{ id: Number(add.dataset.id), quantity: 1 }])
        .catch(() => {
          window.location.href = `${routes}cart`;
        })
        .finally(() => {
          add.disabled = false;
        });
      return;
    }

    const change = event.target.closest('[data-cart-change]');
    if (!change) return;

    const key = change.dataset.lineKey;
    const quantity = Number(change.dataset.quantity);
    if (!key || Number.isNaN(quantity)) return;

    change.disabled = true;
    changeLine(key, quantity)
      .catch(() => {
        // Fall back to the cart page rather than leaving the drawer stale.
        window.location.href = `${routes}cart`;
      })
      .finally(() => {
        change.disabled = false;
      });
  });

  /* ---- Intercept add-to-cart submissions ---- */
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.action || !form.action.includes('/cart/add')) return;

    event.preventDefault();

    const submitter = event.submitter;
    if (submitter) submitter.disabled = true;

    addItem(new FormData(form))
      .catch(() => {
        form.submit();
      })
      .finally(() => {
        if (submitter) submitter.disabled = false;
      });
  });

  /*
   * The only public surface. Everything else stays private — this exists so
   * the product page can add the brush and its add-on through the same code
   * path as every other add, rather than owning a second copy of it.
   */
  window.cartDrawer = { open, addItems };
})();
